/** @jsxImportSource @compiled/react */
import React, { type FC, useCallback, useEffect, useRef } from 'react';
import { cssMap } from '@compiled/react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

// Jest uses classic JSX (`React.createElement`); TS `noUnusedLocals` does not treat JSX as reading `React`.
void React;

import FeedbackCollector from '@atlaskit/feedback-collector/FeedbackCollector';
import FeedbackIcon from '@atlaskit/icon/core/feedback';
import CrossIcon from '@atlaskit/icon/core/cross';
import { RovoIcon } from '@atlaskit/logo';
import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import { Text } from '@atlaskit/primitives/compiled/text';
import { Popup } from '@atlaskit/popup/popup';

import { LazyAnimatedRovoIcon } from '@atlassian/rovo-platform-ui-components/primitives/LazyAnimatedRovoIcon';

import { expVal } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';

const i18n = defineMessages({
	feedbackButtonLabel: {
		id: 'space-overview.smart-space-overview.feedback-button-label',
		defaultMessage: 'Give feedback',
		description:
			'Label for the button shown above the smart space overview cover picture to open the standard feedback dialog.',
	},
	feedbackTitle: {
		id: 'space-overview.smart-space-overview.feedback-title',
		defaultMessage: 'What do you think of the new Spaces overview?',
		description: 'Title shown in the feedback dialog for the smart space overview experience.',
	},
	feedbackPreamble: {
		id: 'space-overview.smart-space-overview.feedback-preamble',
		defaultMessage: 'Help make it even better with your feedback.',
		description: 'Preamble shown in the feedback dialog for the smart space overview experience.',
	},
	organizedByRovo: {
		id: 'space-overview.smart-space-overview.organized-by-rovo',
		defaultMessage: 'Organized by Rovo',
		description:
			'Label shown in the space overview header indicating that the content is organized by Rovo AI. Clickable to open an info popup.',
	},
	popupDismissLabel: {
		id: 'space-overview.smart-space-overview.popup-dismiss-label',
		defaultMessage: 'Dismiss',
		description: 'Accessible label for the button that dismisses the Organized by Rovo info popup.',
	},
	popupDescription: {
		id: 'space-overview.smart-space-overview.popup-description',
		// oxlint-disable-next-line eslint-core/no-restricted-syntax
		defaultMessage: "This is where your team's work comes together.",
		description:
			'Description shown in the Organized by Rovo popup explaining the purpose of the space overview.',
	},
	popupBullet1: {
		id: 'space-overview.smart-space-overview.popup-bullet-1',
		defaultMessage: 'See the summary of the space',
		description: 'First bullet point in the Organized by Rovo popup.',
	},
	popupBullet2: {
		id: 'space-overview.smart-space-overview.popup-bullet-2',
		defaultMessage: 'Pin key documents',
		description: 'Second bullet point in the Organized by Rovo popup.',
	},
	popupBullet3: {
		id: 'space-overview.smart-space-overview.popup-bullet-3',
		// oxlint-disable-next-line eslint-core/no-restricted-syntax
		defaultMessage: "Get answers to your team's most important questions",
		description: 'Third bullet point in the Organized by Rovo popup.',
	},
});

const FEEDBACK_OPENED_STORAGE_KEY = 'smart-space-overview-feedback-opened';
const FEEDBACK_CONTEXT_FIELD_ID = 'customfield_10047';
const FEEDBACK_ENTRYPOINT_ID = '6b7a459d-eab0-4b18-b825-481b90b7a262';
const FEEDBACK_SOURCE = 'smartSpaceOverviewHeader';

const hasFeedbackCollectorBeenOpened = () => {
	try {
		return localStorage.getItem(FEEDBACK_OPENED_STORAGE_KEY) === 'true';
	} catch {
		return false;
	}
};

const persistFeedbackCollectorOpened = () => {
	try {
		localStorage.setItem(FEEDBACK_OPENED_STORAGE_KEY, 'true');
	} catch {
		// ignore
	}
};

export const SmartSpaceOverviewHeader: FC = () => {
	const { formatMessage, locale } = useIntl();
	const { displayName, userId } = useSessionData();
	const autoOpenTimeoutRef = useRef<number | null>(null);
	const [isFeedbackCollectorOpen, setIsFeedbackCollectorOpen] = React.useState(false);
	const [isHovered, setIsHovered] = React.useState(false);
	const [isPopupOpen, setIsPopupOpen] = React.useState(false);

	const togglePopup = useCallback(() => {
		setIsPopupOpen((prev) => !prev);
	}, []);

	const closePopup = useCallback(() => {
		setIsPopupOpen(false);
	}, []);

	const openFeedbackCollector = useCallback(() => {
		persistFeedbackCollectorOpened();

		if (autoOpenTimeoutRef.current !== null) {
			window.clearTimeout(autoOpenTimeoutRef.current);
			autoOpenTimeoutRef.current = null;
		}

		setIsFeedbackCollectorOpen(true);
	}, []);

	const closeFeedbackCollector = useCallback(() => {
		setIsFeedbackCollectorOpen(false);
	}, []);

	useEffect(() => {
		if (expVal('confluence_space_overview_feedback_auto_open_delay', 'value', 0) <= 0) {
			return;
		}

		autoOpenTimeoutRef.current = window.setTimeout(
			() => {
				autoOpenTimeoutRef.current = null;
				if (!hasFeedbackCollectorBeenOpened()) {
					openFeedbackCollector();
				}
			},
			expVal('confluence_space_overview_feedback_auto_open_delay', 'value', 0),
		);

		return () => {
			if (autoOpenTimeoutRef.current !== null) {
				window.clearTimeout(autoOpenTimeoutRef.current);
				autoOpenTimeoutRef.current = null;
			}
		};
	}, [openFeedbackCollector]);

	return (
		<>
			<div css={styles.overviewHeaderActions}>
				<Popup
					isOpen={isPopupOpen}
					onClose={closePopup}
					placement="bottom-start"
					offset={[0, -32]}
					trigger={(triggerProps) => (
						<button
							{...triggerProps}
							type="button"
							css={styles.organizedByRovo}
							onClick={togglePopup}
							onMouseEnter={() => setIsHovered(true)}
							onMouseLeave={() => setIsHovered(false)}
							onFocus={() => setIsHovered(true)}
							onBlur={() => setIsHovered(false)}
						>
							{isHovered ? (
								<LazyAnimatedRovoIcon width="20" height="20" />
							) : (
								<>
									<RovoIcon size="xsmall" appearance="brand" />
									<span aria-hidden="true" css={styles.preload}>
										<LazyAnimatedRovoIcon width="20" height="20" />
									</span>
								</>
							)}
							<FormattedMessage {...i18n.organizedByRovo} />
						</button>
					)}
					content={() => (
						<div css={styles.popupContent}>
							<div css={styles.popupHeader}>
								<span css={styles.popupHeaderLeft}>
									<RovoIcon size="xsmall" appearance="brand" />
									<FormattedMessage {...i18n.organizedByRovo} />
								</span>
								<IconButton
									appearance="subtle"
									icon={CrossIcon}
									label={formatMessage(i18n.popupDismissLabel)}
									onClick={closePopup}
								/>
							</div>
							<div css={styles.popupBody}>
								<Text size="small" color="color.text.subtle">
									<FormattedMessage {...i18n.popupDescription} />
								</Text>
							</div>
							<ul css={styles.popupList}>
								<li>
									<FormattedMessage {...i18n.popupBullet1} />
								</li>
								<li>
									<FormattedMessage {...i18n.popupBullet2} />
								</li>
								<li>
									<FormattedMessage {...i18n.popupBullet3} />
								</li>
							</ul>
						</div>
					)}
				/>
				<Button
					onClick={openFeedbackCollector}
					testId="smart-space-overview-feedback-button"
					css={styles.feedbackButton}
					iconBefore={(iconProps) => <FeedbackIcon {...iconProps} spacing="spacious" label="" />}
				>
					<FormattedMessage {...i18n.feedbackButtonLabel} />
				</Button>
			</div>
			{isFeedbackCollectorOpen && (
				<FeedbackCollector
					locale={locale}
					onClose={closeFeedbackCollector}
					onSubmit={closeFeedbackCollector}
					atlassianAccountId={userId || ''}
					name={displayName || ''}
					entrypointId={FEEDBACK_ENTRYPOINT_ID}
					feedbackTitle={formatMessage(i18n.feedbackTitle)}
					feedbackTitleDetails={formatMessage(i18n.feedbackPreamble)}
					additionalFields={[
						{
							id: FEEDBACK_CONTEXT_FIELD_ID,
							value: FEEDBACK_SOURCE,
						},
					]}
				/>
			)}
		</>
	);
};

const styles = cssMap({
	overviewHeaderActions: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.250'),
		minHeight: token('space.600'),
		paddingBlock: token('space.050'),
	},
	organizedByRovo: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.050'),
		font: token('font.body'),
		color: token('color.text.subtlest'),
		cursor: 'pointer',
		background: 'none',
		border: 'none',
		padding: 0,
		'&:hover': {
			textDecoration: 'underline',
		},
	},
	preload: {
		position: 'absolute',
		width: '1px',
		height: '1px',
		overflow: 'hidden',
		opacity: 0,
		pointerEvents: 'none',
	},
	feedbackButton: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: token('radius.medium'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		backgroundColor: token('elevation.surface'),
		color: token('color.text'),
		paddingBlock: token('space.050'),
		paddingInline: token('space.100'),
		cursor: 'pointer',
	},
	popupContent: {
		padding: token('space.200'),
		maxWidth: '320px',
	},
	popupHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginBlockEnd: token('space.100'),
	},
	popupHeaderLeft: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.050'),
		font: token('font.body.small'),
		fontWeight: token('font.weight.medium'),
		color: token('color.text'),
	},
	popupBody: {
		marginBlockEnd: token('space.050'),
	},
	popupList: {
		font: token('font.body.small'),
		color: token('color.text.subtle'),
		margin: 0,
		paddingInlineStart: token('space.250'),
	},
});
